import { useMemo } from 'react';

import { useGetFilteredClinicalData } from '../data/getClinicalData';

import { calculateRates } from './calculateRates';
import { useProviderCounts } from './providerRates';
import type { TBenchmarkingCounts } from './types';

export const useOverallRates = ({
  practices,
  providers,
}: {
  practices?: string[];
  providers?: string[];
}) => {
  const { data, isLoading } = useGetFilteredClinicalData({
    // If we're only showing one provider, we don't want to filter their data by materiality
    isMaterialityEnabled: (providers?.length ?? 0) !== 1,
  });

  const providerCounts = useProviderCounts(data);

  const filteredProviderCounts = useMemo(() => {
    return providerCounts?.filter(
      (provider) =>
        (!practices?.length ||
          (provider?.practiceId && practices.includes(provider?.practiceId))) &&
        (!providers?.length ||
          (provider?.providerId && providers.includes(provider?.providerId))),
    );
  }, [providerCounts, practices, providers]);

  const initOverallCounts: TBenchmarkingCounts = {
    completedProduction: 0,
    previousCompletedProduction: 0,
    recommendations: {
      planned: 0,
      scheduled: 0,
      completed: 0,
      total: 0,
      unplanned: 0,
      viewedInVidea: 0,
    },
    previousRecommendations: {
      planned: 0,
      scheduled: 0,
      completed: 0,
      total: 0,
      unplanned: 0,
      viewedInVidea: 0,
    },
  };

  const overallCounts = filteredProviderCounts?.reduce((acc, provider) => {
    acc.completedProduction += provider?.completedProduction ?? 0;
    acc.previousCompletedProduction +=
      provider?.previousCompletedProduction ?? 0;
    acc.recommendations.planned += provider?.recommendations.planned ?? 0;
    acc.recommendations.scheduled += provider?.recommendations.scheduled ?? 0;
    acc.recommendations.completed += provider?.recommendations.completed ?? 0;
    acc.recommendations.total += provider?.recommendations.total ?? 0;
    acc.recommendations.unplanned += provider?.recommendations.unplanned ?? 0;
    acc.previousRecommendations.planned +=
      provider?.previousRecommendations.planned ?? 0;
    acc.previousRecommendations.scheduled +=
      provider?.previousRecommendations.scheduled ?? 0;
    acc.previousRecommendations.completed +=
      provider?.previousRecommendations.completed ?? 0;
    acc.previousRecommendations.total +=
      provider?.previousRecommendations.total ?? 0;
    acc.previousRecommendations.unplanned +=
      provider?.previousRecommendations.unplanned ?? 0;
    acc.recommendations.viewedInVidea +=
      provider?.recommendations.viewedInVidea ?? 0;
    acc.previousRecommendations.viewedInVidea +=
      provider?.previousRecommendations.viewedInVidea ?? 0;
    return acc;
  }, initOverallCounts);

  return {
    ...calculateRates(overallCounts),
    isLoading,
  };
};
